<template>
  <div>
    <section class="d-flex justify-content-between align-items-center mb-2 mt-4">
      <div class="category-title">
        <h2 class="mb-0 t-primary position-relative d-inline-block">
          <span class="pr-2">{{ getIndexAsString(state.index) }}</span>
          <span class="t-secondary" v-if="count">{{ count | formatNumber }}</span>
        </h2>
      </div>
    </section>
    <section class="d-flex justify-content-between align-items-center mb-2">
      <h3 class="t-xlr t-grey text-nowrap t-500">
        Landscape view
      </h3>
      <div class="d-flex align-items-center justify-content-end flex-wrap">
        <search-view-toggler
          :activeIndex="state.index"
        />
      </div>
    </section>

    <spinner v-if="processedBoards.length === 0" :containerClass="`width--50px min-height--65px`" />

    <div class="row small-gutters" v-for="(section, i) in processedBoards" :key="i">
      <div class="col" :class="`col-12 col-lg-${board.width} mb-3`" v-for="board in section.boards" :key="board.key">
        <div class="card">
          <div class="card-body">
            <span class="t-600 d-block mb-1 t-overflow"
              v-tooltip="{
                content: board.tooltip,
                classes: ['narrow-tooltip'],
              }"
            >
              <i class="fa ml-1 t-secondary" :class="icons[board.title_icon]" />
              {{ board.title }}
            </span>
            <spinner v-if="board.isLoading" :containerClass="`width--50px min-height--65px`" />

            <!-- {{ board.results ? board.results.length : 'Loading...' }} results -->
            <template v-if="board.board_type === 'map'">
              <template v-if="board.isPreview">
                <img v-if="board.item_type === 'countries'"
                  :src="require('@/assets/img/landscape/landscape_world.svg')"
                  @click="openPreviewModal"
                />
                <img v-else
                  :src="require('@/assets/img/landscape/landscape_us_states.svg')"
                  @click="openPreviewModal"
                />
              </template>
              <template v-else>
                <basic-map
                  v-if="!board.isLoading && board.results"
                  :graphData="board.results"
                  :name="board.title"
                  :mapType="board.item_type"
                  :state="state"
                  :filterName="board.filter_name"
                />
              </template>
              <span v-if="!board.isLoading && !board.results">No results...</span>
            </template>
            <!-- No preview for trends -->
            <template v-else-if="board.board_type === 'trend' && !board.isPreview">
              <div class="row small-gutters mb-3" v-if="!board.isLoading">
                <div class="col-12 col-md-6 col-lg-3"
                  v-for="trend in board.results"
                  :key="trend.name"
                >
                  <trend
                    :state="state"
                    :trend="trend"
                  />
                </div>
              </div>
              <div class="d-flex justify-content-end" v-if="!board.isLoading">
                <a href="#" @click.prevent="openTrendsModal(board)" class="d-flex align-items-center position-absolute" style="bottom: 16px; right: 20px;">
                  <i class="fa fa-arrows-alt t-secondary mr-1" />
                  <span class="t-600 t-sm">See more</span>
                </a>
              </div>
            </template>
            <template v-else-if="board.board_type === 'list'">
              <template v-if="board.isPreview">
                <img :src="require('@/assets/img/landscape/landscape_list.svg')"
                  @click="openPreviewModal"
                >
              </template>
              <template v-else>
                <researcher-list v-if="board.item_type === 'researcher'"
                  :board="board"
                />
                <research-group-list v-if="board.item_type === 'research_group'"
                  :board="board"
                />
                <organisation-list v-if="board.item_type === 'organisation'"
                  :board="board"
                />
              </template>
            </template>
            <template v-else>
              <div v-for="res in board.results" :key="res.data.full_name" class="t-overflow">
                <router-link :to="{
                  name: `${board.item_type}.show`,
                  params: { id: res.data.slug_values[0] }
                }">
                  {{ res.data.full_name || res.data.name }}
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <freemium-cta v-if="!$can('viewLandscape')" />
  </div>
</template>

<script>
import axios from 'axios'

import SearchService from '@/services/search'
import { ICONS } from '@/assets/js/_data'

import Trend from '@/components/shared/chart/Trend'
import BasicMap from '@/components/shared/chart/BasicMap'
import SearchViewToggler from '@/components/search/SearchViewToggler'
import Spinner from '@/components/shared/Spinner'
import ResearcherList from '@/components/search/landscape/ResearcherList'
import ResearchGroupList from '@/components/search/landscape/ResearchGroupList'
import OrganisationList from '@/components/search/landscape/OrganisationList'
import FreemiumCta from '@/components/search/FreemiumCTA'

export default {
  name: 'landscape',
  components: {
    SearchViewToggler,
    Spinner,
    BasicMap,
    Trend,
    ResearcherList,
    ResearchGroupList,
    OrganisationList,
    FreemiumCta
  },
  props: {
    state: {
      type: Object,
      required: true
    },
    isScopedSearch: {
      type: Boolean,
      required: true
    },
    config: {
      type: Array,
      required: true
    },
    count: {
      type: Number,
      required: true
    },
    cancelTokenSource: {
      required: true
    }
  },
  data () {
    return {
      processedBoards: [],
      icons: ICONS,
      batchSize: 3
    }
  },
  mounted () {
    if (this.config.length > 0) {
      this.initData()
    }

    if (!this.$can('viewLandscape')) {
      this.openPreviewModal()
    }
  },
  watch: {
    config (newData, oldData) {
      if (newData.length > 0) {
        this.initData()
      }
    }
  },
  methods: {
    openPreviewModal () {
      this.$events.$emit('modal:landscape:preview')
    },
    async initData () {
      const canViewBoards = this.$can('viewLandscape')
      const processedBoards = this.config.map(el => {
        const newEl = {
          ...el,
          boards: Object.entries(el.boards)
            .map(([key, val]) => {
              return {
                ...val,
                key: key,
                results: [],
                isLoading: canViewBoards, // If canViewBoards = true, we also need to fetch board data
                isPreview: !canViewBoards // If user can't view boards, show preview images
              }
            })
            .sort((a, b) => a.order - b.order)
            .filter(b => {
              if (b.isPreview && b.board_type === 'trend') return false
              return true
            }) // Filter out trend boards for previews
        }
        return newEl
      })

      this.processedBoards = processedBoards

      if (!canViewBoards) return
      // Check for when we navigate to another route, to stop the processing of old data.
      let breakCheck = false

      for (let i = 0; i < this.config.length; i++) {
        if (breakCheck) {
          break
        }
        const boards = Object.keys(this.config[i].boards)
        let batches = Math.floor(boards.length / this.batchSize)
        const remainder = boards.length % this.batchSize
        if (remainder) batches += 1

        const newState = {
          ...this.state,
          page: 0
        }

        for (let j = 0; j < batches; j++) {
          const start = j * this.batchSize
          const end = start + this.batchSize
          newState.boards = processedBoards[i].boards.slice(start, end).map(b => b.key)

          try {
            const response = await SearchService.getDashboardData(newState, this.cancelTokenSource.token)

            Object.entries(response).forEach(([key, val]) => {
              const index = processedBoards[i].boards.findIndex(b => b.key === key)

              if (val.results) {
                this.$set(processedBoards[i].boards[index], 'results', val.results.map(result => {
                  if (result.data && result.data.slug_values) {
                    result.data.slug = result.data.slug_values[0]
                  }
                  return result
                }))
              } else {
                this.$set(processedBoards[i].boards[index], 'results', [])
              }
              this.$set(processedBoards[i].boards[index], 'url', val.url)
            })

            newState.boards.forEach(key => {
              const index = processedBoards[i].boards.findIndex(b => b.key === key)
              this.$set(processedBoards[i].boards[index], 'isLoading', false)
            })
          } catch (e) {
            if (!axios.isCancel(e)) {
              this.isResultLoaded = !this.isEmpty(e)
              this.error = this.handleError(e)
            } else {
              // Break out of loops when we get an axios.isCancel. This means user navigated to another route
              breakCheck = true
              break
            }
          }
        }
      }
      this.processedBoards = processedBoards
    },
    openTrendsModal (board) {
      this.$events.$emit('modal:filter:trends:open', board.filter_name, this.state, board.trend)
    }
  }
}
</script>
